import { PaymentRequestType } from 'types/cashFlow'
import { ReduxActionType } from 'types/global'
import { ExpectedMilestonePaymentType } from 'types/paymentOptions'
import { RootState } from 'types/state'

export const SET_REFRESH_MILESTONE_TRIGGER = 'SET_REFRESH_MILESTONE_TRIGGER'
export const SET_SDK_TOKEN_RERESH = 'SET_SDK_TOKEN_RERESH'
export const SET_PROJECT_MILESTONES_LOADING = 'SET_PROJECT_MILESTONES_LOADING'
export const SAVE_PROJECT_MILESTONES = 'SAVE_PROJECT_MILESTONES'
export const CLEAR_PROJECT_MILESTONES = 'CLEAR_PROJECT_MILESTONES'
export const SAVE_EXPECTED_MILESTONE_PAYMENTS = 'EXPECTED_MILESTONE_PAYMENTS'
export const TOGGLE_SHOW_ARCHIVED_PAYMENTS = 'TOGGLE_SHOW_ARCHIVED_PAYMENTS'

export type ProjectMilstonesReduxType = {
  refreshMilestoneTrigger: string | undefined
  sdkTokenRefresh: string | undefined
  projectMilestones: PaymentRequestType[] | undefined
  /** Project id the cached ``projectMilestones`` list belongs to (avoids cross-project bleed). */
  projectMilestonesProjectId: number | undefined
  expectedMilestonePayments: ExpectedMilestonePaymentType[] | undefined
  milestonesAreLoading: boolean
  showArchivedPayments: boolean
}

const initialState: ProjectMilstonesReduxType = {
  refreshMilestoneTrigger: undefined,
  sdkTokenRefresh: undefined,
  projectMilestones: undefined,
  projectMilestonesProjectId: undefined,
  expectedMilestonePayments: undefined,
  milestonesAreLoading: false,
  showArchivedPayments: false,
}

export const triggerMilestoneRefresh = () => {
  return {
    type: SET_REFRESH_MILESTONE_TRIGGER,
  }
}

export const triggerSDKTokenRefresh = () => {
  return {
    type: SET_SDK_TOKEN_RERESH,
  }
}

export function saveProjectMilestones(milestones: PaymentRequestType[], projectId?: number) {
  return {
    type: SAVE_PROJECT_MILESTONES,
    payload: { milestones, projectId },
  }
}

export const clearProjectMilestones = () => {
  return {
    type: CLEAR_PROJECT_MILESTONES,
  }
}

export const setProjectMilestonesLoading = () => {
  return {
    type: SET_PROJECT_MILESTONES_LOADING,
  }
}

export const saveExpectedMilestonePayments = (expectedMilestonePayments: ExpectedMilestonePaymentType[]) => {
  return {
    type: SAVE_EXPECTED_MILESTONE_PAYMENTS,
    payload: { expectedMilestonePayments },
  }
}

export const toggleShowArchivedPayments = () => {
  return {
    type: TOGGLE_SHOW_ARCHIVED_PAYMENTS,
  }
}

export default (state = initialState, action: ReduxActionType) => {
  switch (action.type) {
    case SET_REFRESH_MILESTONE_TRIGGER:
      return {
        ...state,
        refreshMilestoneTrigger: `${new Date()}`,
        projectMilestones: undefined,
        projectMilestonesProjectId: undefined,
        milestonesAreLoading: false,
      }
    case SET_SDK_TOKEN_RERESH:
      return {
        ...state,
        sdkTokenRefresh: `${new Date()}`,
      }
    case CLEAR_PROJECT_MILESTONES:
      return {
        ...state,
        projectMilestones: undefined,
        projectMilestonesProjectId: undefined,
        milestonesAreLoading: false,
      }
    case SAVE_PROJECT_MILESTONES:
      return {
        ...state,
        projectMilestones: action.payload.milestones,
        projectMilestonesProjectId: action.payload.projectId,
        milestonesAreLoading: false,
      }
    case SET_PROJECT_MILESTONES_LOADING:
      return {
        ...state,
        milestonesAreLoading: true,
      }
    case SAVE_EXPECTED_MILESTONE_PAYMENTS:
      return {
        ...state,
        expectedMilestonePayments: action.payload.expectedMilestonePayments,
      }
    case TOGGLE_SHOW_ARCHIVED_PAYMENTS:
      return {
        ...state,
        showArchivedPayments: !state.showArchivedPayments,
      }
    default:
      return { ...state }
  }
}

export type ProjectMilestonesSelectorsType = {
  getRefreshMilestonesTrigger: (state: RootState) => ProjectMilstonesReduxType['refreshMilestoneTrigger']
  getSDKTokenRefreshTrigger: (state: RootState) => ProjectMilstonesReduxType['sdkTokenRefresh']
  getProjectMiletones: (state: RootState) => ProjectMilstonesReduxType['projectMilestones']
  getProjectMilestonesProjectId: (state: RootState) => ProjectMilstonesReduxType['projectMilestonesProjectId']
  getMilestonesAreLoading: (state: RootState) => ProjectMilstonesReduxType['milestonesAreLoading']
  getExpectedMilestonePayments: (state: RootState) => ProjectMilstonesReduxType['expectedMilestonePayments']
  getShowArchivedPayments: (state: RootState) => ProjectMilstonesReduxType['showArchivedPayments']
}

export const projectMilestonesSelectors: ProjectMilestonesSelectorsType = {
  getRefreshMilestonesTrigger: (state: RootState) => {
    return state.projectMilestones.refreshMilestoneTrigger
  },
  getSDKTokenRefreshTrigger: (state: RootState) => {
    return state.projectMilestones.sdkTokenRefresh
  },
  getProjectMiletones: (state: RootState) => {
    return state.projectMilestones.projectMilestones
  },
  getProjectMilestonesProjectId: (state: RootState) => {
    return state.projectMilestones.projectMilestonesProjectId
  },
  getMilestonesAreLoading: (state: RootState) => {
    return state.projectMilestones.milestonesAreLoading
  },
  getExpectedMilestonePayments: (state: RootState) => {
    return state.projectMilestones.expectedMilestonePayments
  },
  getShowArchivedPayments: (state: RootState) => {
    return state.projectMilestones.showArchivedPayments
  },
}
